import * as React from 'react';
import { noop } from 'vet-bones/utils';

import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { LayoutAlgorithmType } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';
import { projectSelector } from 'src/selectors/project';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type ProjectBaseSettingsValue = {
  dataNamespace: string;
  description: string;
  disableVoiceboxSuggestions: boolean;
  name: string;
  projectLayout: LayoutAlgorithmType;
  propertyType: ProjectPropertyType;
  handleResetBaseSettings: () => void;
  setDataNamespace: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  setDisableVoiceboxSuggestions: React.Dispatch<React.SetStateAction<boolean>>;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setProjectLayout: React.Dispatch<React.SetStateAction<LayoutAlgorithmType>>;
  setPropertyType: React.Dispatch<React.SetStateAction<ProjectPropertyType>>;
};

export const ProjectBaseSettingsContext =
  React.createContext<ProjectBaseSettingsValue>({
    dataNamespace: '',
    description: '',
    disableVoiceboxSuggestions: false,
    name: '',
    projectLayout: LayoutAlgorithmType.D3_FORCE_LAYOUT,
    propertyType: DEFAULT_PROJECT_PROPERTY_TYPE,
    handleResetBaseSettings: noop,
    setDataNamespace: noop,
    setDescription: noop,
    setDisableVoiceboxSuggestions: noop,
    setName: noop,
    setProjectLayout: noop,
    setPropertyType: noop,
  });

export const useProjectBaseSettings = () => {
  const contextMenu = React.useContext(ProjectBaseSettingsContext);
  return contextMenu;
};

export const useProjectBaseSettingsValue = (): ProjectBaseSettingsValue => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();

  const project = useReduxSelector(projectSelector(projectId));

  const [dataNamespace, setDataNamespace] = React.useState(
    project?.dataNamespace || ''
  );
  const [description, setDescription] = React.useState(
    project?.description || ''
  );
  const [name, setName] = React.useState(project?.name || '');
  const [projectLayout, setProjectLayout] = React.useState(
    diagram?.model.modelData.layoutAlgorithm ||
      LayoutAlgorithmType.D3_FORCE_LAYOUT
  );
  const [propertyType, setPropertyType] = React.useState<ProjectPropertyType>(
    project?.propertyType || DEFAULT_PROJECT_PROPERTY_TYPE
  );
  const [disableVoiceboxSuggestions, setDisableVoiceboxSuggestions] =
    React.useState(Boolean(project?.disableVoiceboxSuggestions));

  const handleResetBaseSettings = React.useCallback(() => {
    setName(project?.name || '');
    setDescription(project?.description || '');
    setDataNamespace(project?.dataNamespace || '');
  }, [project]);

  return {
    dataNamespace,
    description,
    disableVoiceboxSuggestions,
    name,
    projectLayout,
    propertyType,
    handleResetBaseSettings,
    setDataNamespace,
    setDescription,
    setDisableVoiceboxSuggestions,
    setName,
    setProjectLayout,
    setPropertyType,
  };
};
